import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Printer, X, ExternalLink } from "lucide-react";

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  buyer_name: string;
  buyer_address?: string;
  buyer_current_on_contract: boolean;
  buyer_payment_history?: string;
  date_of_sale?: string;
  first_payment_date?: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
  interest_rate?: number;
  payment_amount?: number;
  original_term_months?: number;
  has_balloon_payment: boolean;
  balloon_amount?: number;
  balloon_due_date?: string;
  property_type?: string;
  property_description?: string;
  property_address?: string;
  estimated_property_value?: number;
  current_balance?: number;
  payments_remaining?: number;
  status: string;
  bank_notes?: string;
  created_at: string;
  zillow_link?: string;
  zillow_researched_at?: string;
}

interface BankSummaryReportProps {
  deal: Deal;
  onClose: () => void;
}

const BankSummaryReport = ({ deal, onClose }: BankSummaryReportProps) => {
  const handlePrint = () => {
    const content = document.getElementById('print-only-version');
    if (!content) {
      window.print();
      return;
    }
    // Print via hidden iframe so the app's global print CSS (which hides
    // everything except dialogs) doesn't blank out this report.
    const styleHtml = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((n) => n.outerHTML)
      .join('\n');

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, {
      position: 'fixed',
      right: '0',
      bottom: '0',
      width: '0',
      height: '0',
      border: '0',
    } as CSSStyleDeclaration);
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      window.print();
      return;
    }
    doc.open();
    doc.write(`<!doctype html><html><head><title>Bank Summary Report</title>
${styleHtml}
<style>
  @page { size: letter; margin: 0.35in; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { font-family: ui-sans-serif, system-ui, sans-serif; color: #111; font-size: 9pt; line-height: 1.25; }
  /* Neutralize the app's global print rules inside this iframe */
  body * { display: revert !important; }

  /* Condensed one-page layout — 2-column masonry so cards flow side by side */
  #print-only-version { max-width: 100% !important; margin: 0 !important;
    column-count: 2; column-gap: 10px; column-fill: balance;
  }
  /* The centered print header should span both columns */
  #print-only-version > .hidden { column-span: all; -webkit-column-span: all; }
  #print-only-version > * { break-inside: avoid; page-break-inside: avoid; display: block; margin: 0 0 6px 0 !important; }


  /* Cards -> flat sections */
  #print-only-version [class*="rounded-lg"][class*="border"] {
    border: 1px solid #d1d5db !important;
    border-radius: 4px !important;
    box-shadow: none !important;
    padding: 4px 8px !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  #print-only-version [class*="rounded-lg"] > div { padding: 2px 0 !important; }

  /* Card headers */
  #print-only-version h3, #print-only-version [class*="CardTitle"], #print-only-version .text-2xl {
    font-size: 10pt !important;
    font-weight: 700 !important;
    margin: 0 0 3px 0 !important;
    padding: 0 !important;
    line-height: 1.2 !important;
  }

  /* Grids tighter */
  #print-only-version .grid { gap: 4px 10px !important; }
  #print-only-version .gap-4 { gap: 4px 10px !important; }
  #print-only-version .mt-4 { margin-top: 4px !important; }
  #print-only-version .my-4 { margin-top: 4px !important; margin-bottom: 4px !important; }
  #print-only-version .space-y-6 > * + * { margin-top: 6px !important; }
  #print-only-version .space-y-4 > * + * { margin-top: 4px !important; }

  /* Label/value pairs */
  #print-only-version p { margin: 0 !important; }
  #print-only-version .text-sm { font-size: 7.5pt !important; }
  #print-only-version .text-xl { font-size: 10pt !important; }
  #print-only-version .text-lg { font-size: 10pt !important; }
  #print-only-version .text-2xl { font-size: 12pt !important; }

  /* Header block */
  #print-only-version .hidden.print\\:block { display: block !important; text-align: center; margin-bottom: 6px !important; }

  /* Badges compact */
  #print-only-version [class*="Badge"], #print-only-version .inline-flex {
    font-size: 7pt !important;
    padding: 1px 5px !important;
  }

  /* Separator */
  #print-only-version hr, #print-only-version [role="separator"] { margin: 3px 0 !important; }
</style>

</head><body>${content.outerHTML}</body></html>`);
    doc.close();

    const doPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => iframe.remove(), 1000);
      }
    };
    // Give styles a tick to apply
    if (doc.readyState === 'complete') {
      setTimeout(doPrint, 200);
    } else {
      iframe.onload = () => setTimeout(doPrint, 200);
    }
  };


  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value}%`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateLTV = () => {
    if (!deal.current_balance || !deal.estimated_property_value) return 'N/A';
    const ltv = (deal.current_balance / deal.estimated_property_value) * 100;
    return `${ltv.toFixed(1)}%`;
  };

  return (
    <div id="print-only-version" className="max-w-4xl mx-auto space-y-6 print:space-y-4">

      {/* Header - Hidden when printing */}
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-2xl font-bold">Bank Summary Report</h1>
        <div className="flex gap-2">
          <Button onClick={handlePrint} variant="outline" size="sm">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-2xl font-bold">Land Contract Investment Opportunity</h1>
        <p className="text-lg text-muted-foreground">{deal.deal_title}</p>
        <p className="text-sm text-muted-foreground">
          Deal entered {new Date(deal.created_at).toLocaleDateString()} • Generated on {new Date().toLocaleDateString()}
        </p>

      </div>

      {/* Executive Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Executive Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Current Balance</p>
              <p className="text-xl font-bold text-primary">{formatCurrency(deal.current_balance)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly Payment</p>
              <p className="text-xl font-bold">{formatCurrency(deal.payment_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Property Value</p>
              <p className="text-xl font-bold">{formatCurrency(deal.estimated_property_value)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ITV Ratio</p>
              <p className="text-xl font-bold">{calculateLTV()}</p>
            </div>
          </div>
          
          <Separator className="my-4" />
          
          <div className="flex items-center gap-4">
            <Badge variant={deal.buyer_current_on_contract ? "default" : "destructive"}>
              {deal.buyer_current_on_contract ? "Current on Payments" : "Behind on Payments"}
            </Badge>
            <Badge variant="outline">{deal.property_type || 'N/A'}</Badge>
            {deal.has_balloon_payment && (
              <Badge variant="secondary">Balloon Payment</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Property Information */}
      <Card>
        <CardHeader>
          <CardTitle>Property Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Property Address</p>
              <p className="font-medium">{deal.property_address || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Property Type</p>
              <p className="font-medium">{deal.property_type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Estimated Value</p>
              <p className="font-medium">{formatCurrency(deal.estimated_property_value)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ITV Ratio</p>
              <p className="font-medium">{calculateLTV()}</p>
            </div>
          </div>
          {deal.property_description && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="font-medium">{deal.property_description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Original Contract Terms */}
      <Card>
        <CardHeader>
          <CardTitle>Original Contract Terms</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Date of Sale</p>
              <p className="font-medium">{formatDate(deal.date_of_sale)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">First Payment Date</p>
              <p className="font-medium">{formatDate(deal.first_payment_date)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Original Sales Price</p>
              <p className="font-medium">{formatCurrency(deal.sales_price)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Down Payment</p>
              <p className="font-medium">{formatCurrency(deal.down_payment_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Beginning Balance</p>
              <p className="font-medium">{formatCurrency(deal.beginning_balance)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Interest Rate</p>
              <p className="font-medium">{formatPercent(deal.interest_rate)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly Payment</p>
              <p className="font-medium">{formatCurrency(deal.payment_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Original Term</p>
              <p className="font-medium">{deal.original_term_months ? `${deal.original_term_months} months` : 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle>Current Contract Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Current Balance</p>
              <p className="font-medium">{formatCurrency(deal.current_balance)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Payments Remaining</p>
              <p className="font-medium">{deal.payments_remaining || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Payment Status</p>
              <Badge variant={deal.buyer_current_on_contract ? "default" : "destructive"}>
                {deal.buyer_current_on_contract ? "Current" : "Behind"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Balloon Payment Information */}
      {deal.has_balloon_payment && (
        <Card>
          <CardHeader>
            <CardTitle>Balloon Payment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Balloon Amount</p>
                <p className="font-medium">{formatCurrency(deal.balloon_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Balloon Due Date</p>
                <p className="font-medium">{formatDate(deal.balloon_due_date)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Buyer Information */}
      <Card>
        <CardHeader>
          <CardTitle>Buyer Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Buyer Name</p>
              <p className="font-medium">{deal.buyer_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Buyer Address</p>
              <p className="font-medium">{deal.buyer_address || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Payment Status</p>
              <Badge variant={deal.buyer_current_on_contract ? "default" : "destructive"}>
                {deal.buyer_current_on_contract ? "Current on Contract" : "Behind on Payments"}
              </Badge>
            </div>
          </div>
          {deal.buyer_payment_history && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">Payment History Summary</p>
              <p className="font-medium">{deal.buyer_payment_history}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Zillow Research */}
      {deal.zillow_link && (
        <Card>
          <CardHeader>
            <CardTitle>Property Research</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {deal.zillow_researched_at && (
                <p className="text-sm text-muted-foreground">
                  Researched on {new Date(deal.zillow_researched_at).toLocaleDateString()}
                </p>
              )}
              <a 
                href={deal.zillow_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                View Property on Zillow
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Notes */}
      {deal.bank_notes && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Notes</p>
            <p className="font-medium">{deal.bank_notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Footer - Hidden when printing */}
      <div className="print:hidden text-center text-sm text-muted-foreground">
        <p>This summary was generated on {new Date().toLocaleDateString()} for bank review purposes.</p>
      </div>
    </div>
  );
};

export default BankSummaryReport;